<template>
  <div class="nft-card">
    <div class="logo flex mobile:hidden">
      <img
        :src="nft.logo"
        :alt="`${nft.name} logo`"
      >
      <div class="shine-container">
        <div class="shine -rotate-45" />
        <div class="shine rotate-0" />
        <div class="shine rotate-45" />
        <div class="shine rotate-90" />
      </div>
    </div>
    <div class="flex h-full w-full flex-col gap-y-4">
      <div class="flex w-full justify-between gap-x-2 text-primary">
        <div class="logo mr-2 hidden mobile:flex">
          <img
            :src="nft.logo"
            :alt="`${nft.name} logo`"
          >
          <div class="shine-container">
            <div class="shine -rotate-45" />
            <div class="shine rotate-0" />
            <div class="shine rotate-45" />
            <div class="shine rotate-90" />
          </div>
        </div>
        <div class="flex flex-col gap-y-1">
          <span class="h-fit w-fit rounded-2xl bg-primary/40 px-2 py-0 text-base font-bold text-background">
            {{ nft.tokenTypeName }}
          </span>
          <div class="flex flex-wrap items-center gap-x-2 mobile:flex-col mobile:items-start mobile:gap-y-1">
            <strong class="break-all text-2xl mobile:text-[20px]">{{ nft.name }}</strong>
            <span class="h-fit rounded bg-primary/10 px-2 py-1 text-[20px] mobile:text-lg">{{ nft.symbol }}</span>
          </div>
        </div>
        <BcIconLink
          icon="external-link-square-alt"
          :to="routes.getNftDetailPageWithTab(nft.type, nft.address)"
          :size="isMobile ? 'xs' : 'sm'"
        />
      </div>
      <p class="description">
        {{ nft.description }}
      </p>
      <div class="info-container">
        <div class="info">
          <span class="label">Min Price</span>
          <p class="flex flex-wrap items-center gap-x-2 break-all font-bold">
            {{ nft.minPrice }}
            <span class="font-normal text-primary/40">{{ TOKEN_UNIT }}</span>
          </p>
        </div>
        <div class="info">
          <span class="label">Volume (24H)</span>
          <p class="flex flex-wrap items-center gap-x-2 break-all font-bold">
            {{ nft.volume }}
            <span class="font-normal text-primary/40">{{ TOKEN_UNIT }}</span>
            <Volatility
              :value="nft.volumeChange"
              unit="%"
            >
              <template #prefix>
                <span>(</span>
              </template>
              <template #suffix>
                <span>)</span>
              </template>
            </Volatility>
          </p>
        </div>
        <div class="info">
          <span class="label">Holders</span>
          <p class="flex flex-wrap items-center gap-x-2 break-all font-bold">
            {{ nft.holders }}
            <Volatility
              :value="nft.holdersChange"
              unit="%"
            >
              <template #prefix>
                <span>(</span>
              </template>
              <template #suffix>
                <span>)</span>
              </template>
            </Volatility>
          </p>
        </div>
        <div class="info">
          <span class="label">Total Supply</span>
          <p class="break-all font-bold">
            {{ nft.totalSupply }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Volatility from '~/components/text/Volatility.vue'
import { useResponsiveScreen } from '~/composables/useResponsiveScreen'
import { TOKEN_UNIT } from '~/environment-variables/unit.const'
import { NftCardModel } from '~/types/model/nft.model'
import BcIconLink from '~/ui/IconLink/BcIconLink.vue'

interface IProps {
  nft: NftCardModel
}

defineProps<IProps>()

const { isMobile } = useResponsiveScreen()
</script>

<style scoped lang="scss">
.nft-card {
  @apply flex w-full gap-x-8 rounded-2xl bg-primary/5 p-8 text-primary;

  @include sm {
    @apply flex-col gap-y-4;
  }
}

.logo {
  @apply relative h-32 w-32 p-4 shrink-0 items-center justify-center rounded-full bg-primary/10;

  @include md {
    @apply w-24 h-24;
  }

  @include sm {
    @apply w-16 h-16;
  }
}

.shine-container {
  @apply absolute top-0 left-0 w-full h-full flex items-center justify-center -z-[1];
  content: '';
  animation: {
    name: spin;
    duration: 3s;
    iteration-count: infinite;
    timing-function: linear;
  };

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .shine {
    @apply absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 blur;

    &::before, &::after {
      @apply absolute opacity-20 border-transparent border-x-8 border-y-[1rem];
      content: '';
      transform: scaleY(4.5);

      @include md {
        transform: scaleY(4);
      }

      @include sm {
        transform: scaleY(3.5);
      }
    }

    &::before {
      @apply -bottom-[100px] -right-2 border-t-primary;

      @include md {
        @apply -bottom-[90px];
      }

      @include sm {
        @apply -bottom-[75px];
      }
    }

    &::after {
      @apply -top-[100px] -left-2 border-b-primary;

      @include md {
        @apply -top-[90px];
      }

      @include sm {
        @apply -top-[75px];
      }
    }
  }
}

.description {
  @apply text-primary/40 text-lg;
}

.info-container {
  @apply grid grid-cols-2 gap-2;

  @include sm {
    @apply grid-cols-1;
  }

  .info {
    @apply flex flex-col gap-y-1;

    .label {
      @apply text-base text-primary/40;
    }
  }
}
</style>